import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

export type AuthModalView = 'login' | 'register' | 'forgot-password' | 'username'

interface AuthModalContextValue {
	view: AuthModalView | null
	isOpen: boolean
	isUsernameRequired: boolean
	openLogin: () => void
	openRegister: () => void
	close: () => void
	setView: (view: AuthModalView) => void
	requireUsername: () => void
	completeUsername: () => void
}

const AuthModalContext = createContext<AuthModalContextValue | undefined>(undefined)

const CLOSE_ANIMATION_MS = 200

export function AuthModalProvider({ children }: { children: ReactNode }) {
	const [view, setViewState] = useState<AuthModalView | null>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [isUsernameRequired, setIsUsernameRequired] = useState(false)

	const openLogin = useCallback(() => {
		setViewState('login')
		setIsOpen(true)
	}, [])

	const openRegister = useCallback(() => {
		setViewState('register')
		setIsOpen(true)
	}, [])

	const close = useCallback(() => {
		if (isUsernameRequired) {
			return
		}

		setIsOpen(false)
		window.setTimeout(() => {
			setViewState(null)
		}, CLOSE_ANIMATION_MS)
	}, [isUsernameRequired])

	const setView = useCallback((nextView: AuthModalView) => {
		setViewState(nextView)
		setIsOpen(true)
	}, [])

	const requireUsername = useCallback(() => {
		setIsUsernameRequired(true)
		setViewState('username')
		setIsOpen(true)
	}, [])

	const completeUsername = useCallback(() => {
		setIsUsernameRequired(false)
		setIsOpen(false)
		window.setTimeout(() => {
			setViewState(null)
		}, CLOSE_ANIMATION_MS)
	}, [])

	const value = useMemo(
		() => ({
			view,
			isOpen,
			isUsernameRequired,
			openLogin,
			openRegister,
			close,
			setView,
			requireUsername,
			completeUsername,
		}),
		[view, isOpen, isUsernameRequired, openLogin, openRegister, close, setView, requireUsername, completeUsername],
	)

	return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>
}

export function useAuthModal() {
	const context = useContext(AuthModalContext)
	if (!context) {
		throw new Error('useAuthModal must be used within an AuthModalProvider')
	}
	return context
}
